import { useCallback, useReducer, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { uploadCourseCertificatesBatch } from "@/api/courseCertificates";
import {
  invalidateCourseCertificateListQueries,
  invalidateCourseCertificateQueries,
} from "@/features/cle-compliance/hooks/invalidateCleDocumentQueries";
import type { CourseCertificateUploadError } from "@/features/cle-compliance/models/courseCertificate";
import { sortUploadErrorsBySelectionIndex } from "@/features/cle-compliance/utils/mergeUploadErrors";
import { planCourseCertificateUpload } from "@/features/cle-compliance/utils/planCourseCertificateUpload";
import {
  runCourseCertificateUpload,
  type ChunkSettlement,
} from "@/features/cle-compliance/utils/runCourseCertificateUpload";

export type CourseCertificateUploadRunStatus =
  "idle" | "uploading" | "done" | "cancelled";

type State = {
  status: CourseCertificateUploadRunStatus;
  totalFiles: number;
  settledFiles: number;
  receivedFiles: number;
  errors: CourseCertificateUploadError[];
};

type Action =
  | {
      type: "start";
      totalFiles: number;
      errors: CourseCertificateUploadError[];
    }
  | { type: "preflight-only"; errors: CourseCertificateUploadError[] }
  | { type: "chunk-settled"; settlement: ChunkSettlement }
  | { type: "finish"; status: "done" | "cancelled" };

const INITIAL_STATE: State = {
  status: "idle",
  totalFiles: 0,
  settledFiles: 0,
  receivedFiles: 0,
  errors: [],
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "start":
      return {
        status: "uploading",
        totalFiles: action.totalFiles,
        settledFiles: 0,
        receivedFiles: 0,
        errors: action.errors,
      };
    case "preflight-only":
      return { ...INITIAL_STATE, status: "done", errors: action.errors };
    case "chunk-settled":
      return {
        ...state,
        settledFiles: state.settledFiles + action.settlement.settledCount,
        receivedFiles:
          state.receivedFiles + action.settlement.outcome.receivedCount,
        errors: sortUploadErrorsBySelectionIndex([
          ...state.errors,
          ...action.settlement.outcome.errors,
        ]),
      };
    case "finish":
      return { ...state, status: action.status };
  }
};

/** Uploads a selection of course certificates as several small concurrent
 * requests instead of one large one, so the table fills in progressively and
 * the user is never blocked.
 *
 * The run deliberately survives unmounting: switching tabs in HomeView tears
 * this component down, and finishing the upload with the progress banner gone
 * is a better outcome than abandoning it half-done. Only the explicit cancel
 * aborts. */
export const useCourseCertificateBatchUpload = () => {
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);
  const runningRef = useRef(false);

  const start = useCallback(
    (files: File[]) => {
      // A ref, not state: start may fire twice before a re-render.
      if (runningRef.current) {
        return;
      }

      const plan = planCourseCertificateUpload(files);

      if (plan.chunks.length === 0) {
        dispatch({ type: "preflight-only", errors: plan.preflightErrors });

        return;
      }

      runningRef.current = true;
      const controller = new AbortController();
      abortRef.current = controller;

      dispatch({
        type: "start",
        totalFiles: plan.acceptedCount,
        errors: plan.preflightErrors,
      });

      void (async () => {
        try {
          await runCourseCertificateUpload({
            chunks: plan.chunks,
            signal: controller.signal,
            upload: uploadCourseCertificatesBatch,
            onChunkSettled: (settlement) => {
              dispatch({ type: "chunk-settled", settlement });

              // Reveal this chunk's rows now. List only: the reporting
              // aggregates cannot move until analysis finishes.
              invalidateCourseCertificateListQueries(queryClient);
            },
          });
        } finally {
          const wasCancelled = controller.signal.aborted;
          runningRef.current = false;
          abortRef.current = null;

          // Once, at the end: rows may have landed that the per-chunk list
          // refresh missed (an aborted chunk, say).
          invalidateCourseCertificateQueries(queryClient);
          dispatch({
            type: "finish",
            status: wasCancelled ? "cancelled" : "done",
          });
        }
      })();
    },
    [queryClient],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return {
    ...state,
    /** Accepted files whose chunk never went out, after a cancel. */
    skippedFiles: Math.max(state.totalFiles - state.settledFiles, 0),
    isUploading: state.status === "uploading",
    start,
    cancel,
  };
};
